import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card, Button, Badge, Notice, AsyncBoundary, useToast, ConfirmDialog,
} from '@kollez/design-system';
import {
  useExports, useCreateExport, useDeletionStatus, useRequestDeletion, useCancelDeletion,
} from '@/lib/queries';

/**
 * /settings/privacy (F73) — DPDP data-subject rights UI:
 *   - Download my data (create export → download link)
 *   - Delete my account (60-day cancellable window)
 */
export function PrivacyPage() {
  const exports = useExports();
  const createExport = useCreateExport();
  const deletion = useDeletionStatus();
  const requestDeletion = useRequestDeletion();
  const cancelDeletion = useCancelDeletion();
  const toast = useToast();
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  async function doExport() {
    try {
      const r = await createExport.mutateAsync();
      toast.show({ message: 'Export ready — starting download.' });
      if (r.downloadUrl) window.open(r.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch {
      toast.show({ tone: 'warning', message: 'Export failed. Try again in a minute.' });
    }
  }

  async function doRequestDeletion() {
    try {
      await requestDeletion.mutateAsync({ reason: deleteReason || undefined });
      toast.show({ message: 'Deletion scheduled. You have 60 days to cancel.' });
      setShowConfirm(false); setDeleteReason('');
    } catch {
      toast.show({ tone: 'warning', message: 'Could not schedule deletion.' });
    }
  }

  async function doCancelDeletion() {
    try {
      await cancelDeletion.mutateAsync();
      toast.show({ message: 'Deletion cancelled.' });
    } catch {
      toast.show({ tone: 'warning', message: 'Could not cancel.' });
    }
  }

  const pendingDelete = deletion.data?.request?.status === 'pending' ? deletion.data.request : null;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Privacy · DPDP</p>
          <h1 className="h-page">Your data, your rules</h1>
          <p className="subhead">Under India's Digital Personal Data Protection Act, you can download or delete your data at any time.</p>
        </div>
      </div>

      {/* Page 63/N — DPDP §13/§14/§6 rights are legally required and were
          previously undiscoverable from Privacy. Deep-links added. */}
      <div className="grid gap-2 md:grid-cols-3">
        <Link to="/settings/privacy/grievances" className="card card-pad">
          <div className="eyebrow">§13(3) · 90-day SLA</div>
          <div className="cell-title">File a grievance</div>
          <div className="cell-sub">Complaint about how your data was handled.</div>
        </Link>
        <Link to="/settings/privacy/rectification" className="card card-pad">
          <div className="eyebrow">§14 · Rectify</div>
          <div className="cell-title">Correct my data</div>
          <div className="cell-sub">Ask the fiduciary to fix inaccurate records held on you.</div>
        </Link>
        <Link to="/settings/privacy/consents" className="card card-pad">
          <div className="eyebrow">§6 · Consents</div>
          <div className="cell-title">Manage consents</div>
          <div className="cell-sub">View + revoke consents you've granted.</div>
        </Link>
      </div>

      {/* Deletion status banner */}
      {pendingDelete && (
        <Notice tone="warning">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <div>
              <b>Deletion scheduled for {new Date(pendingDelete.effectiveAt).toLocaleDateString('en-IN')}.</b>
              {' '}You can cancel any time before that date.
            </div>
            <Button variant="primary" onClick={doCancelDeletion} loading={cancelDeletion.isPending}>Cancel deletion</Button>
          </div>
        </Notice>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        <Card padding="lg">
          <h2 className="h-section mb-2">Download my data</h2>
          <p className="text-ink-secondary text-sm mb-3">
            A JSON snapshot of your profile, applications, offers, interviews, notifications, mock sessions, and resumes.
            Kollez keeps the file for 30 days.
          </p>
          <Button variant="primary" onClick={doExport} loading={createExport.isPending}>Request export</Button>

          <AsyncBoundary kind="table" loading={exports.isLoading} error={exports.error}
            empty={!exports.isLoading && !exports.data?.exports.length}
            emptyTitle="No exports yet" emptyHint="Hit Request export above."
            onRetry={() => void exports.refetch()}>
            <ul className="grid gap-2 mt-4 text-sm">
              {exports.data?.exports.map((e) => (
                <li key={e._id} className="p-2 rounded border border-line-primary flex items-baseline justify-between gap-2">
                  <div>
                    <div>{new Date(e.requestedAt).toLocaleString('en-IN')}</div>
                    <div className="text-xs text-ink-tertiary">
                      {e.status === 'ready' && e.fileSizeBytes ? `${(e.fileSizeBytes / 1024).toFixed(1)} KB` : e.failureReason ?? ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone={e.status === 'ready' ? 'success' : e.status === 'failed' ? 'warning' : 'outline'}>{e.status}</Badge>
                    {e.status === 'ready' && e.downloadUrl && (
                      <a href={e.downloadUrl} target="_blank" rel="noopener noreferrer" className="text-ink-info underline">Download</a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </AsyncBoundary>
        </Card>

        <Card padding="lg">
          <h2 className="h-section mb-2">Delete my account</h2>
          <p className="text-ink-secondary text-sm mb-3">
            Deletion runs 60 days after you request it. You can cancel any time before then.
          </p>
          <Notice tone="info" className="mb-3">
            <b>What we keep (anonymized, required by law):</b>
            <ul className="list-disc list-inside mt-1 text-xs">
              <li>Placement outcomes for NIRF (AICTE audit norm)</li>
              <li>Audit log entries for 7 years (DPDP + AICTE audit norms)</li>
            </ul>
          </Notice>
          <Button
            variant="danger"
            onClick={() => setShowConfirm(true)}
            disabled={Boolean(pendingDelete)}
          >
            {pendingDelete ? 'Deletion already scheduled' : 'Request deletion'}
          </Button>
        </Card>
      </div>

      <ConfirmDialog
        open={showConfirm}
        onOpenChange={setShowConfirm}
        title="Delete your Kollez account?"
        description="You have 60 days to cancel. After that, your profile, resumes, applications, and offers will be removed. Placement outcomes will be kept anonymized for NIRF."
        confirmText="Yes, schedule deletion"
        cancelText="Never mind"
        tone="danger"
        onConfirm={doRequestDeletion}
      >
        <label className="grid gap-1 mt-2">
          <span className="text-xs uppercase tracking-wide text-ink-tertiary">Reason (optional, helps us improve)</span>
          <textarea value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} rows={3} className="input" />
        </label>
      </ConfirmDialog>
    </div>
  );
}
